import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView, trackEvent } from "@/lib/tracking";
import { setAbandonedCheckoutAttribution } from "@/lib/abandonedCheckoutAttribution";

/**
 * Site-wide analytics capture:
 *  - page_view on every route change
 *  - scroll depth milestones (25/50/75/100)
 *  - time-on-page on unload
 *  - outbound link clicks
 */
export function useAnalytics() {
  const location = useLocation();
  const enteredAt = useRef<number>(Date.now());
  const milestones = useRef<Set<number>>(new Set());

  // Page view + reset per-route trackers
  useEffect(() => {
    enteredAt.current = Date.now();
    milestones.current = new Set();
    trackPageView(location.pathname + location.search);

    // Abandoned-checkout retry click — fires when a user lands from the
    // abandoned-checkout email (utm_medium=abandoned_checkout). Dedup per
    // sid+price via sessionStorage so a soft nav or refresh doesn't refire.
    try {
      const params = new URLSearchParams(location.search);
      if (params.get("utm_medium") === "abandoned_checkout") {
        const priceId = params.get("price");
        const email = params.get("email");
        const sid = params.get("sid");
        const campaign = params.get("utm_campaign") ?? undefined;
        const source = params.get("utm_source") ?? undefined;
        const dedupKey = `abx_click:${sid ?? ""}:${priceId ?? ""}`;
        if (!sessionStorage.getItem(dedupKey)) {
          sessionStorage.setItem(dedupKey, "1");
          trackEvent("abandoned_checkout_retry_click", {
            price_id: priceId ?? undefined,
            customer_email: email ?? undefined,
            stripe_session_id: sid ?? undefined,
            utm_campaign: campaign,
            utm_source: source,
            utm_medium: "abandoned_checkout",
            landing_path: location.pathname,
          });
          // Persist attribution so the checkout return page can fire a
          // completion event tied to the original abandoned session + price.
          setAbandonedCheckoutAttribution({
            price_id: priceId ?? undefined,
            stripe_session_id: sid ?? undefined,
            utm_campaign: campaign,
            utm_source: source,
          });
          // Strip PII (email) from the visible URL so it doesn't linger in
          // referrers, browser history, or downstream analytics page_view.
          if (email) {
            params.delete("email");
            const cleaned = params.toString();
            const nextUrl = location.pathname + (cleaned ? `?${cleaned}` : "") + location.hash;
            window.history.replaceState({}, "", nextUrl);
          }
        }
      }
    } catch {
      /* never let analytics break UX */
    }
  }, [location.pathname, location.search]);

  // Scroll depth
  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled = window.scrollY + window.innerHeight;
      const pct = Math.min(100, Math.round((scrolled / doc.scrollHeight) * 100));
      [25, 50, 75, 100].forEach((m) => {
        if (pct >= m && !milestones.current.has(m)) {
          milestones.current.add(m);
          trackEvent("scroll_depth", { depth: m, path: location.pathname });
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  // Outbound clicks + time-on-page
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const a = (e.target as HTMLElement)?.closest("a") as HTMLAnchorElement | null;
      if (!a || !a.href) return;
      try {
        const url = new URL(a.href);
        if (url.host && url.host !== window.location.host) {
          trackEvent("outbound_click", { url: a.href, host: url.host });
        }
      } catch {
        /* noop */
      }
    };
    const onUnload = () => {
      const seconds = Math.round((Date.now() - enteredAt.current) / 1000);
      trackEvent("time_on_page", { seconds, path: location.pathname });
    };
    document.addEventListener("click", onClick);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      document.removeEventListener("click", onClick);
      window.removeEventListener("beforeunload", onUnload);
    };
  }, [location.pathname]);
}
